
<b-card
  body-class="p-0"
  header-class="p-3"
  :class="{ 'table-loading': loading }"
>
  <template v-if="!headless" #header>
    <b-col v-if="label">
      <h4 class="card-header-title">
        <Icon
          v-if="icon"
          :icon="icon"
          class="text-muted mr-2 mt-4 ml-n3"
          size="22"
          style="margin-left: -5px"
        />
        {{ label }}
      </h4>
    </b-col>

    <b-col v-else>
      <form @submit.prevent="refreshTable">
        <div class="input-group input-group-flush">
          <div class="input-group-prepend">
            <span class="input-group-text">
              <i class="fe fe-search"></i>
            </span>
          </div>

          <input
            v-model="parameters.q"
            class="form-control search"
            type="search"
            :placeholder="__('common.search')"
            data-testid="table-search-input"
          />
        </div>
      </form>
    </b-col>

    <b-col
      cols="auto"
      class="pr-0 pl-0 btn-group-sm d-flex align-items-center"
    >
      <slot name="tableHeaderActions" />

      <div v-if="hasFilters" class="filters">
        <DataFilter
          :filters="filters"
          :inline="inlineFilters"
          @applyFilters="filterTable"
          @clearFilters="clearFilters"
        >
          <template #filters>
            <slot name="filters" />
          </template>
        </DataFilter>
      </div>

      <slot v-if="actionPopoverCondition(tableData)" name="actionsPopover" />
    </b-col>
  </template>

  <div class="table-responsive">
    <template v-if="loading">
      <div class="absolute-centered">
        <b-spinner
          variant="primary"
          type="grow"
          label="Spinning"
          class="full-opacity"
        ></b-spinner>
      </div>
    </template>

    <table
      :aria-multiselect="selectionEnabled"
      class="table table-sm table-nowrap card-table table-hover"
      :class="tableClass"
      data-testid="section-list-card"
    >
      <thead>
        <tr>
          <th v-if="selectionEnabled" class="selectable-header">
            <Checkbox
              label=""
              :inline="false"
              :name="`${id}-isSelectAllRows`"
              class="mb-0 mr-0"
              :checked="isSelectAllRows"
              :disabled="!tableData || !tableData.length"
              @onChange="onSelectAllCheckboxChange"
            />
          </th>
          <template v-for="(column, index) in columns">
            <th
              v-if="column.visibleIf ? column.visibleIf() : true"
              :key="index"
              :class="{ sortable: column.sortable, nowrap: column.nowrap }"
              :style="column.style"
              :colspan="column.colspan"
            >
              <template v-if="column.sortable">
                <div
                  v-if="
                    parameters.sort.startsWith('-') &&
                    parameters.sort.includes(
                      column.sortColumn ? column.sortColumn : column.column
                    )
                  "
                  class="sortable"
                  @click="
                    sort(
                      column.sortColumn ? column.sortColumn : column.column
                    )
                  "
                >
                  {{ column.title }}
                  <Icon size="12" icon="sort-amount-down"></Icon>
                </div>

                <div
                  v-else-if="
                    !parameters.sort.startsWith('-') &&
                    parameters.sort.includes(
                      column.sortColumn ? column.sortColumn : column.column
                    )
                  "
                  class="sortable"
                  @click="
                    sort(
                      column.sortColumn ? column.sortColumn : column.column
                    )
                  "
                >
                  {{ column.title }}
                  <Icon size="12" icon="sort-amount-up"></Icon>
                </div>

                <div
                  v-else
                  class="sortable"
                  @click="
                    sort(
                      column.sortColumn ? column.sortColumn : column.column
                    )
                  "
                >
                  {{ column.title }}
                  <Icon size="12" icon="sort"></Icon>
                </div>
              </template>

              <template v-else>
                {{ column.title }}
              </template>
            </th>
          </template>

          <th
            v-if="hasRowActions"
            style="width: 5%"
            :colspan="actionsColumnColspan"
          ></th>
        </tr>
      </thead>

      <tbody class="list">
        <template v-if="tableData && tableData.length <= 0">
          <tr>
            <td
              :colspan="columns.length + (hasRowActions ? 1 : 0)"
              class="text-center text-muted"
            >
              <small>{{ __('common.no-results') }}</small>
            </td>
          </tr>
        </template>

        <template v-else>
          <template v-for="(item, rowIndex) in computedTableData">
            <tr
              :key="rowIndex"
              role="row"
              :style="rowClickHandler ? 'cursor: pointer' : ''"
              @click="rowClickHandler && !$event.target.closest('button, a, input, label, select, [role=\'button\']') ? rowClickHandler(item) : null"
            >
              <td v-if="getSelectable(item)">
                <Checkbox
                  :name="`${id}-selectRow-${rowIndex}`"
                  label=""
                  class="mb-n1 mr-0"
                  :checked="isRowSelected(item)"
                  @onChange="
                    (event) => {
                      onRowClick(event, item, rowIndex)
                    }
                  "
                />
              </td>
              <template v-for="(column, columnIndex) in columns">
                <td
                  v-if="column.visibleIf ? column.visibleIf() : true"
                  :key="columnIndex"
                  :colspan="column.colspan"
                >
                  <slot
                    v-if="!column.fromArrayItem"
                    :name="column.column"
                    :item="item"
                  />

                  <template v-if="column.custom && column.fromArrayItem">
                    {{
                      lodash.get(
                        item,
                        `${column.fromArrayItem}.${column.column}`
                      )
                    }}
                  </template>

                  <template v-if="!column.custom">
                    {{ lodash.get(item, column.column) }}
                  </template>
                </td>
              </template>

              <td
                v-if="hasRowActions"
                :colspan="actionsColumnColspan"
                class="text-right"
              >
                <slot
                  ref="actionButtons"
                  name="actionButtons"
                  :item="item"
                ></slot>
              </td>
            </tr>
            <slot name="afterItem" :item="item" />
            <slot name="childItem" :item="item" />
          </template>
        </template>
      </tbody>
      <tfoot>
        <slot name="tfoot" :meta="meta" />
      </tfoot>
    </table>
  </div>

  <template #footer>
    <b-row align-content="center">
      <b-pagination
        v-if="meta.perPage < totalRecords"
        v-model="currentPage"
        size="sm"
        class="col-6 mb-0"
        pills
        :total-rows="totalRecords"
        :per-page="meta.perPage"
        @page-click="changePage"
      />

      <b-col
        :cols="paginationCols"
        :class="{
          'justify-content-between': selectionEnabled,
          'justify-content-end': !selectionEnabled,
        }"
        class="d-flex align-items-center"
      >
        <small v-if="selectionEnabled" class="text-muted">
          {{ selectionText }}
        </small>
        <small
          data-testid="table-pagination-text-label"
          class="text-muted d-flex align-items-center"
        >
          <SelectField
            v-if="isPaginationLinksDisplayed"
            id="per_page"
            class="mr-4 mb-0 p-0"
            name="per_page"
            label=""
            required
            :is-pagination-select="true"
            first-by-default
            :options="perPageSelectValues"
            @onChange="(item) => changePerPage(item.value)"
          />
          <p class="mt-0 mb-0">{{ paginationText }}</p>
        </small>
      </b-col>
    </b-row>
  </template>
</b-card>
